import React, { useState, useCallback, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { AppHeader } from '../../shared/components/AppComponents';
import { apiFetchJson } from '../../apiClient';
import ChoreBoard from './ChoreBoard';
import GoalPlanningBoard from './GoalPlanningBoard';
import RoomBoard from './RoomBoard';
import FocusMode from './FocusMode';
import MomentumBar from './MomentumBar';
import ChoreCreateModal from './ChoreCreateModal';
import ChoreTemplateManager from './ChoreTemplateManager';
import ChoreSettings from './ChoreSettings';
import { useTranslation } from 'react-i18next';
import './ChoreApp.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

function ChoreApp({ user }) {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('rooms');
  const [chores, setChores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [roomSummaries, setRoomSummaries] = useState([]);
  const [focusRoomId, setFocusRoomId] = useState(null);
  const [momentum, setMomentum] = useState(null);
  const [deadlinePresets, setDeadlinePresets] = useState([]);
  const [goals, setGoals] = useState([]);
  const [householdGoals, setHouseholdGoals] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingChore, setEditingChore] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [message, setMessage] = useState(null);

  const showMessage = useCallback((text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const data = await apiFetchJson(instance, '/api/chore-categories');
      setCategories(unwrap(data));
    } catch (e) { console.error('Failed to load categories:', e); }
  }, [instance]);

  const loadPresets = useCallback(async () => {
    try {
      const data = await apiFetchJson(instance, '/api/deadline-presets');
      setDeadlinePresets(unwrap(data));
    } catch (e) { console.error('Failed to load presets:', e); }
  }, [instance]);

  const loadMomentum = useCallback(async () => {
    try {
      const data = await apiFetchJson(instance, '/api/chores/momentum');
      setMomentum(data);
    } catch (e) { console.error('Failed to load momentum:', e); }
  }, [instance]);

  const loadRooms = useCallback(async () => {
    try {
      const [roomData, summaryData] = await Promise.all([
        apiFetchJson(instance, '/api/rooms'),
        apiFetchJson(instance, '/api/rooms/summary').catch(() => null)
      ]);
      setRooms(unwrap(roomData));
      if (summaryData) setRoomSummaries(unwrap(summaryData));
    } catch (e) { console.error('Failed to load rooms:', e); }
  }, [instance]);

  const loadChores = useCallback(async () => {
    try {
      setLoading(true);
      let url = '/api/chores';
      const params = [];
      if (statusFilter !== null) params.push(`status=${statusFilter}`);
      if (params.length) url += '?' + params.join('&');

      const data = await apiFetchJson(instance, url);
      setChores(unwrap(data));
    } catch (e) {
      console.error('Failed to load chores:', e);
    } finally {
      setLoading(false);
    }
  }, [instance, statusFilter]);

  const loadGoals = useCallback(async () => {
    try {
      const [myData, householdData] = await Promise.all([
        apiFetchJson(instance, '/api/chore-goals').catch(() => null),
        apiFetchJson(instance, '/api/chore-goals/household').catch(() => null)
      ]);
      if (myData) setGoals(unwrap(myData));
      if (householdData) setHouseholdGoals(unwrap(householdData));
    } catch (e) { console.error('Failed to load goals:', e); }
  }, [instance]);

  const loadMembers = useCallback(async () => {
    try {
      const data = await apiFetchJson(instance, '/api/household-groups');
      // A user can belong to several households; the picker wants one flat,
      // de-duplicated list of people.
      const byUserId = new Map();
      unwrap(data).forEach(household => {
        unwrap(household.members).forEach(m => {
          if (m.userId && !byUserId.has(m.userId)) {
            byUserId.set(m.userId, { userId: m.userId, name: m.userName || m.userEmail || '?' });
          }
        });
      });
      setMembers([...byUserId.values()]);
    } catch (e) { console.error('Failed to load household members:', e); }
  }, [instance]);

  useEffect(() => {
    loadCategories();
    loadPresets();
    loadRooms();
    loadChores();
    loadGoals();
    loadMembers();
    loadMomentum();
  }, [loadCategories, loadPresets, loadRooms, loadChores, loadGoals, loadMembers, loadMomentum]);

  const handleClaimChore = useCallback(async (choreId) => {
    try {
      await apiFetchJson(instance, `/api/chores/${choreId}/claim`, { method: 'POST' });
      loadChores(); showMessage(t('chores.messages.choreClaimed'));
    } catch (e) { console.error('Failed to claim:', e); }
  }, [instance, loadChores, showMessage, t]);

  const handleUnclaimChore = useCallback(async (choreId) => {
    try {
      await apiFetchJson(instance, `/api/chores/${choreId}/unclaim`, { method: 'POST' });
      loadChores(); showMessage(t('chores.messages.choreUnclaimed'));
    } catch (e) { console.error('Failed to unclaim:', e); }
  }, [instance, loadChores, showMessage, t]);

  const handleAssignChore = useCallback(async (choreId, userId) => {
    try {
      await apiFetchJson(instance, `/api/chores/${choreId}/assign`, {
        method: 'POST',
        body: JSON.stringify({ userId })
      });
      loadChores();
      const name = members.find(m => m.userId === userId)?.name;
      showMessage(t('chores.messages.choreAssigned', { name }));
    } catch (e) { console.error('Failed to assign:', e); }
  }, [instance, loadChores, showMessage, members, t]);

  const handleCompleteChore = useCallback(async (choreId) => {
    try {
      await apiFetchJson(instance, `/api/chores/${choreId}/complete`, { method: 'POST' });
      loadChores(); loadGoals(); loadRooms(); loadMomentum(); showMessage(t('chores.messages.choreCompleted'));
    } catch (e) { console.error('Failed to complete:', e); }
  }, [instance, loadChores, loadGoals, loadRooms, loadMomentum, showMessage, t]);

  const handleQuickCapture = useCallback(async ({ titles, roomId }) => {
    try {
      await apiFetchJson(instance, '/api/chores/quick-capture', {
        method: 'POST',
        body: JSON.stringify({ titles, roomId })
      });
      await loadChores();
      loadRooms();
      showMessage(t('chores.messages.captured', { count: titles.length }));
    } catch (e) { console.error('Failed to quick-capture:', e); }
  }, [instance, loadChores, loadRooms, showMessage, t]);

  const handleSetEstimate = useCallback(async (choreId, estimatedMinutes) => {
    try {
      await apiFetchJson(instance, `/api/chores/${choreId}/estimate`, {
        method: 'PUT',
        body: JSON.stringify({ estimatedMinutes })
      });
      loadChores(); loadRooms();
    } catch (e) { console.error('Failed to set estimate:', e); }
  }, [instance, loadChores, loadRooms]);

  const handleReopenChore = useCallback(async (choreId) => {
    try {
      await apiFetchJson(instance, `/api/chores/${choreId}/reopen`, { method: 'POST' });
      loadChores(); showMessage(t('chores.messages.choreReopened'));
    } catch (e) { console.error('Failed to reopen:', e); }
  }, [instance, loadChores, showMessage, t]);

  const handleDeleteChore = useCallback(async (choreId) => {
    try {
      await apiFetchJson(instance, `/api/chores/${choreId}`, { method: 'DELETE' });
      loadChores(); loadGoals(); showMessage(t('chores.messages.choreDeleted'));
    } catch (e) { console.error('Failed to delete:', e); }
  }, [instance, loadChores, loadGoals, showMessage, t]);

  const handleToggleChecklistItem = useCallback(async (choreId, itemId, isCompleted) => {
    try {
      await apiFetchJson(instance, `/api/chores/${choreId}/checklist/${itemId}`, {
        method: 'PUT',
        body: JSON.stringify({ isCompleted })
      });
      loadChores(); loadGoals();
    } catch (e) { console.error('Failed to toggle checklist item:', e); }
  }, [instance, loadChores, loadGoals]);

  const handleAddChecklistItem = useCallback(async (choreId, title) => {
    try {
      await apiFetchJson(instance, `/api/chores/${choreId}/checklist`, {
        method: 'POST',
        body: JSON.stringify({ title })
      });
      loadChores();
    } catch (e) { console.error('Failed to add checklist item:', e); }
  }, [instance, loadChores]);

  const handleDeleteChecklistItem = useCallback(async (choreId, itemId) => {
    try {
      await apiFetchJson(instance, `/api/chores/${choreId}/checklist/${itemId}`, {
        method: 'DELETE'
      });
      loadChores();
    } catch (e) { console.error('Failed to delete checklist item:', e); }
  }, [instance, loadChores]);

  const handleAddGoal = useCallback(async (choreId, bucket) => {
    try {
      await apiFetchJson(instance, '/api/chore-goals', {
        method: 'POST',
        body: JSON.stringify({ choreId, bucket })
      });
      loadGoals(); showMessage(t('chores.messages.addedToPlan'));
    } catch (e) { console.error('Failed to add goal:', e); }
  }, [instance, loadGoals, showMessage, t]);

  const handleMoveGoal = useCallback(async (goalId, bucket) => {
    try {
      await apiFetchJson(instance, `/api/chore-goals/${goalId}`, {
        method: 'PUT',
        body: JSON.stringify({ bucket })
      });
      loadGoals();
    } catch (e) { console.error('Failed to move goal:', e); }
  }, [instance, loadGoals]);

  const handleRemoveGoal = useCallback(async (goalId) => {
    try {
      await apiFetchJson(instance, `/api/chore-goals/${goalId}`, { method: 'DELETE' });
      loadGoals();
    } catch (e) { console.error('Failed to remove goal:', e); }
  }, [instance, loadGoals]);

  const handleCreateChore = useCallback(async (choreData) => {
    try {
      await apiFetchJson(instance, '/api/chores', {
        method: 'POST',
        body: JSON.stringify(choreData)
      });
      loadChores();
      setShowCreateModal(false);
      showMessage(t('chores.messages.choreCreated'));
    } catch (e) { console.error('Failed to create chore:', e); }
  }, [instance, loadChores, showMessage, t]);

  const handleUpdateChore = useCallback(async (choreId, choreData) => {
    try {
      await apiFetchJson(instance, `/api/chores/${choreId}`, {
        method: 'PUT',
        body: JSON.stringify(choreData)
      });
      loadChores();
      setEditingChore(null);
      showMessage(t('chores.messages.choreUpdated'));
    } catch (e) { console.error('Failed to update chore:', e); }
  }, [instance, loadChores, showMessage, t]);

  const openChoreCount = chores.filter(c => c.status !== 2).length;

  const tabs = [
    { id: 'rooms', label: `🏠 ${t('chores.tabs.rooms')}`, count: openChoreCount },
    { id: 'focus', label: `⚡ ${t('chores.tabs.focus')}` },
    { id: 'board', label: `📋 ${t('chores.tabs.board')}`, count: chores.length },
    { id: 'plan', label: `🎯 ${t('chores.tabs.plan')}`, count: goals.length },
    { id: 'templates', label: `🔄 ${t('chores.tabs.recurring')}` },
    { id: 'settings', label: `⚙️ ${t('chores.tabs.settings')}` }
  ];

  return (
    <div className="chore-app">
      <AppHeader title={t('chores.title')} user={user} />

      {message && (
        <div className={`chore-message chore-message-${message.type}`} role="status" aria-live="polite">
          {message.text}
        </div>
      )}

      <div className="chore-tabs" role="tablist" aria-label="Chore views">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`chore-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            role="tab"
            aria-selected={activeTab === tab.id}
          >
            {tab.label}
            {tab.count !== undefined && <span className="chore-tab-count">{tab.count}</span>}
          </button>
        ))}
      </div>

      {(activeTab === 'rooms' || activeTab === 'focus') && (
        <MomentumBar momentum={momentum} onRefresh={loadMomentum} />
      )}

      {activeTab === 'rooms' && (
        <RoomBoard
          rooms={rooms}
          chores={chores}
          roomSummaries={roomSummaries}
          loading={loading}
          onQuickCapture={handleQuickCapture}
          onSetEstimate={handleSetEstimate}
          onComplete={handleCompleteChore}
          onEdit={(chore) => setEditingChore(chore)}
          onSelectRoomForFocus={(roomId) => { setFocusRoomId(roomId); setActiveTab('focus'); }}
        />
      )}

      {activeTab === 'focus' && (
        <FocusMode
          chores={chores}
          rooms={rooms}
          initialRoomId={focusRoomId}
          loading={loading}
          onComplete={handleCompleteChore}
          onSetEstimate={handleSetEstimate}
        />
      )}

      {activeTab === 'board' && (
        <ChoreBoard
          chores={chores}
          categories={categories}
          rooms={rooms}
          loading={loading}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          onClaim={handleClaimChore}
          onUnclaim={handleUnclaimChore}
          onAssign={handleAssignChore}
          members={members}
          onComplete={handleCompleteChore}
          onReopen={handleReopenChore}
          onDelete={handleDeleteChore}
          onToggleChecklistItem={handleToggleChecklistItem}
          onAddChecklistItem={handleAddChecklistItem}
          onDeleteChecklistItem={handleDeleteChecklistItem}
          onAddGoal={handleAddGoal}
          onEdit={(chore) => setEditingChore(chore)}
          onCreateNew={() => setShowCreateModal(true)}
          goals={goals}
        />
      )}

      {activeTab === 'plan' && (
        <GoalPlanningBoard
          goals={goals}
          householdGoals={householdGoals}
          chores={chores}
          onMoveGoal={handleMoveGoal}
          onRemoveGoal={handleRemoveGoal}
          onAddGoal={handleAddGoal}
          onComplete={handleCompleteChore}
          onToggleChecklistItem={handleToggleChecklistItem}
        />
      )}

      {activeTab === 'templates' && (
        <ChoreTemplateManager
          instance={instance}
          categories={categories}
          deadlinePresets={deadlinePresets}
          rooms={rooms}
          onChoresGenerated={loadChores}
          showMessage={showMessage}
        />
      )}

      {activeTab === 'settings' && (
        <ChoreSettings
          instance={instance}
          categories={categories}
          deadlinePresets={deadlinePresets}
          onCategoriesChanged={loadCategories}
          onPresetsChanged={loadPresets}
          showMessage={showMessage}
        />
      )}

      {(showCreateModal || editingChore) && (
        <ChoreCreateModal
          chore={editingChore}
          categories={categories}
          rooms={rooms}
          deadlinePresets={deadlinePresets}
          onSave={editingChore
            ? (data) => handleUpdateChore(editingChore.id, data)
            : handleCreateChore
          }
          onClose={() => { setShowCreateModal(false); setEditingChore(null); }}
        />
      )}
    </div>
  );
}

export default ChoreApp;
